import { z } from 'zod'
import {
  type Bootstrap,
  type Contact,
  type MediaEntry,
  accountSchema,
  contactSchema,
  mediaEntrySchema,
} from './schema.js'

export * from './schema.js'

/** 부트스트랩이 실패해도 청첩장은 열려야 한다. 그때 쓰는 빈 상태. */
export const EMPTY_BOOTSTRAP: Bootstrap = {
  media: [],
  contact: {},
  snapUploadOpenAt: null,
}

/**
 * 최상위 모양만 본다. 배열 안쪽은 항목별로 따로 검사한다 — 문서 전체를 한 번에
 * parse하면 미디어 한 줄에 키가 하나 빠진 것만으로 예외가 위로 튀고, 호출자가
 * 그것을 삼켜 사진 0장·연락처 0건·영상 0개짜리 청첩장이 나온다. 하객 눈에는
 * 완전한 장애와 구별되지 않는다. 손상은 손상된 만큼만 번져야 한다.
 */
const envelopeSchema = z.object({
  media: z.array(z.unknown()),
  contact: z.unknown(),
  snapUploadOpenAt: z.string().nullable(),
})

const telSchema = z.object({ tel: z.string() })

function keepValid<T>(schema: z.ZodType<T>, items: unknown): T[] {
  if (!Array.isArray(items)) return []
  const kept: T[] = []
  for (const item of items) {
    const parsed = schema.safeParse(item)
    if (parsed.success) kept.push(parsed.data)
  }
  return kept
}

function parseContact(raw: unknown): Contact {
  // 온전한 연락처는 통째로 통과시킨다. 어긋나는 순간에만 조각으로 내려가 건질 것을 건진다.
  const whole = contactSchema.safeParse(raw)
  if (whole.success) return whole.data
  if (typeof raw !== 'object' || raw === null) return {}

  const source = raw as Record<string, unknown>
  const accounts = (
    typeof source.accounts === 'object' && source.accounts !== null ? source.accounts : {}
  ) as Record<string, unknown>

  const groomTel = telSchema.safeParse(source.groom)
  const brideTel = telSchema.safeParse(source.bride)
  const groomAccounts = keepValid(accountSchema, accounts.groom)
  const brideAccounts = keepValid(accountSchema, accounts.bride)

  return {
    ...(groomTel.success ? { groom: groomTel.data } : {}),
    ...(brideTel.success ? { bride: brideTel.data } : {}),
    ...(groomAccounts.length > 0 || brideAccounts.length > 0
      ? {
          accounts: {
            ...(groomAccounts.length > 0 ? { groom: groomAccounts } : {}),
            ...(brideAccounts.length > 0 ? { bride: brideAccounts } : {}),
          },
        }
      : {}),
  }
}

export function parseBootstrap(raw: unknown): Bootstrap {
  const envelope = envelopeSchema.parse(raw)
  return {
    media: keepValid(mediaEntrySchema, envelope.media),
    contact: parseContact(envelope.contact),
    snapUploadOpenAt: envelope.snapUploadOpenAt,
  }
}

export async function fetchBootstrap(signal?: AbortSignal): Promise<Bootstrap> {
  const response = await fetch('/api/bootstrap.json', { signal, cache: 'no-store' })
  if (!response.ok) throw new Error(`bootstrap http ${response.status}`)
  return parseBootstrap(await response.json())
}

export function mediaByTier(bootstrap: Bootstrap, tier: MediaEntry['tier']): MediaEntry[] {
  return bootstrap.media.filter((entry) => entry.tier === tier)
}

export function galleryEntries(bootstrap: Bootstrap): MediaEntry[] {
  return bootstrap.media.filter((entry) => entry.key.startsWith('gallery-'))
}

export function mediaByKey(bootstrap: Bootstrap, key: string): MediaEntry | undefined {
  return bootstrap.media.find((entry) => entry.key === key)
}
